import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert, Stack, Chip, Divider, Grid
} from '@mui/material';
import { Sensors as ScanIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  formatAssetLocationLabeledRows,
  formatCustodianForDisplay,
  formatSerialForDisplay
} from '../../utils/assetLocationDisplay';
import { useAuth } from '../../contexts/AuthContext';

const fmt = (n) => Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-PK') : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-PK') : '—');
const statusColor = (status) => {
  if (status === 'active') return 'success';
  if (status === 'disposed') return 'error';
  if (status === 'fully_depreciated') return 'default';
  return 'default';
};

export default function ScanAssetPage() {
  const { tagCode: tagCodeParam } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const tagCode = tagCodeParam ? decodeURIComponent(tagCodeParam) : '';

  const load = useCallback(async () => {
    if (!tagCode) return;
    setLoading(true);
    setError('');
    try {
      const enc = encodeURIComponent(tagCode);
      const res = user
        ? await api.get(`/asset-tagging/resolve/${enc}`)
        : await api.get(`/public/asset-tagging/resolve/${enc}`);
      setData(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Tag not found');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tagCode, user]);

  useEffect(() => {
    if (!tagCode) {
      setLoading(false);
      return;
    }
    if (authLoading) return;
    load();
  }, [tagCode, user, authLoading, load]);

  if (loading) {
    return (
      <Box p={4} textAlign="center"><CircularProgress /></Box>
    );
  }

  const asset = data?.asset;
  const transferHistory = data?.transferHistory || [];
  const assetLocationRows = asset ? formatAssetLocationLabeledRows(asset.location) : [];
  const characteristicRows = [
    { label: 'Brand', value: asset?.brand },
    { label: 'Model', value: asset?.model },
    { label: 'Manufacturer', value: asset?.manufacturer },
    { label: 'Condition', value: asset?.condition },
    { label: 'Warranty Expiry', value: fmtDate(asset?.warrantyExpiryDate) !== '—' ? fmtDate(asset?.warrantyExpiryDate) : '' }
  ].filter((row) => String(row.value || '').trim());
  const projectLabel = asset?.project
    ? [asset.project.code, asset.project.name].filter(Boolean).join(' - ') || asset.project.projectId || asset.project.name
    : '';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: { xs: '100%', sm: 560 }, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" gap={1} mb={2}>
        <ScanIcon color="primary" />
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>Asset lookup</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {asset && (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>Tag code</Typography>
              <Typography variant="h6" sx={{ fontFamily: 'monospace', lineHeight: 1.2 }}>{data.tag?.tagCode}</Typography>
            </Box>
            <Chip
              label={asset.status?.replace(/_/g, ' ')}
              color={statusColor(asset.status)}
              size="small"
              sx={{ textTransform: 'capitalize' }}
            />
          </Stack>
          <Divider sx={{ my: 1.2 }} />

          <Box sx={{ mb: 1.2 }}>
            <Typography variant="caption" color="text.secondary">Asset #</Typography>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>{asset.assetNumber}</Typography>
            <Typography variant="body1" fontWeight={600}>{asset.name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              Category: {asset.category}
            </Typography>
          </Box>

          <Grid container spacing={1.2} sx={{ mb: 1.2 }}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary">Custodian</Typography>
                <Typography variant="body2" fontWeight={600}>{formatCustodianForDisplay(asset.assignedTo) || '—'}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary">Serial</Typography>
                <Typography variant="body2" fontWeight={600}>{formatSerialForDisplay(asset.serialNumber) || '—'}</Typography>
              </Paper>
            </Grid>
            {projectLabel ? (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">Project</Typography>
                  <Typography variant="body2" fontWeight={600}>{projectLabel}</Typography>
                </Paper>
              </Grid>
            ) : null}
            {user ? (
              <>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">Book value</Typography>
                    <Typography variant="body2" fontWeight={700}>PKR {fmt(asset.currentBookValue)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">Purchase date</Typography>
                    <Typography variant="body2" fontWeight={600}>{fmtDate(asset.purchaseDate)}</Typography>
                  </Paper>
                </Grid>
              </>
            ) : null}
          </Grid>

          <Box sx={{ color: 'text.secondary', mb: 1 }}>
            <Typography variant="subtitle2" color="text.primary" fontWeight={700} gutterBottom>
              Location
            </Typography>
            {assetLocationRows.length ? (
              <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5, typography: 'body2' }}>
                {assetLocationRows.map((row, idx) => (
                  <Typography key={`loc-${idx}-${row.label}`} component="li" variant="body2" color="text.secondary">
                    <strong>{row.label}:</strong> {row.value}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">—</Typography>
            )}
          </Box>

          {(characteristicRows.length > 0 || String(asset?.characteristics || '').trim()) && (
            <Box sx={{ color: 'text.secondary', mb: 1 }}>
              <Typography variant="subtitle2" color="text.primary" fontWeight={700} gutterBottom>
                Asset Characteristics
              </Typography>
              {characteristicRows.length ? (
                <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5, typography: 'body2' }}>
                  {characteristicRows.map((row, idx) => (
                    <Typography key={`char-${idx}-${row.label}`} component="li" variant="body2" color="text.secondary">
                      <strong>{row.label}:</strong> {row.value}
                    </Typography>
                  ))}
                </Stack>
              ) : null}
              {String(asset?.characteristics || '').trim() ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: characteristicRows.length ? 0.75 : 0 }}>
                  <strong>Specifications:</strong> {asset.characteristics}
                </Typography>
              ) : null}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Transfer History
          </Typography>
          {transferHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No transfer history yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {transferHistory.map((ev) => {
                const rawPrev = ev?.meta?.previous?.location;
                const rawCurr = ev?.meta?.current?.location;
                const prevRows = rawPrev != null && String(rawPrev).trim()
                  ? formatAssetLocationLabeledRows(rawPrev)
                  : [];
                const currRows = rawCurr != null && String(rawCurr).trim()
                  ? formatAssetLocationLabeledRows(rawCurr)
                  : [];
                const prevCustodian = ev?.meta?.previous?.assignedTo || '—';
                const currCustodian = ev?.meta?.current?.assignedTo || '—';
                const changedBy = ev?.user
                  ? `${ev.user.firstName || ''} ${ev.user.lastName || ''}`.trim() || ev.user.email || '—'
                  : '—';
                return (
                  <Paper key={ev._id} variant="outlined" sx={{ p: 1.25 }}>
                    <Typography variant="caption" color="text.secondary">
                      {fmtDateTime(ev.createdAt)} • by {changedBy}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.75, mb: 0.5 }}>
                      Location
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="flex-start">
                      <Box flex={1} sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">From</Typography>
                        {prevRows.length ? (
                          <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2, typography: 'body2' }}>
                            {prevRows.map((row, idx) => (
                              <Typography key={`pf-${ev._id}-${idx}`} component="li" variant="body2">
                                <strong>{row.label}:</strong> {row.value}
                              </Typography>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2">—</Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, pt: 1.25, color: 'text.secondary' }} aria-hidden>
                        →
                      </Typography>
                      <Box flex={1} sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">To</Typography>
                        {currRows.length ? (
                          <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2, typography: 'body2' }}>
                            {currRows.map((row, idx) => (
                              <Typography key={`pt-${ev._id}-${idx}`} component="li" variant="body2">
                                <strong>{row.label}:</strong> {row.value}
                              </Typography>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2">—</Typography>
                        )}
                      </Box>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Custodian:</strong> {prevCustodian} → {currCustodian}
                    </Typography>
                    {ev.note ? (
                      <Typography variant="caption" color="text.secondary">
                        Note: {ev.note}
                      </Typography>
                    ) : null}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>
      )}

      {user ? (
        <Button sx={{ mt: 2 }} onClick={() => navigate('/asset-tagging/assets')}>← Tagged assets</Button>
      ) : null}
    </Box>
  );
}
